import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Header } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { formatDate } from '../../lib/utils';
import {
  Users,
  Building2,
  CreditCard,
  TrendingUp,
  Loader2,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminDashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/dashboard`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch admin stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header
        title="Admin Dashboard"
        subtitle="System overview and management"
      />

      <div className="flex-1 p-4 lg:p-8 space-y-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="card-base animate-slide-up" style={{ animationDelay: '0ms' }}>
            <CardContent className="p-4 lg:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Tenants</p>
                  <p className="text-2xl lg:text-3xl font-bold font-heading mt-1">{stats?.total_tenants || 0}</p>
                </div>
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Building2 className="w-5 h-5 lg:w-6 lg:h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-base animate-slide-up" style={{ animationDelay: '50ms' }}>
            <CardContent className="p-4 lg:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Users</p>
                  <p className="text-2xl lg:text-3xl font-bold font-heading mt-1">{stats?.total_users || 0}</p>
                </div>
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-secondary/10 flex items-center justify-center">
                  <Users className="w-5 h-5 lg:w-6 lg:h-6 text-secondary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-base animate-slide-up" style={{ animationDelay: '100ms' }}>
            <CardContent className="p-4 lg:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Free Tier</p>
                  <p className="text-2xl lg:text-3xl font-bold font-heading mt-1">{stats?.free_tier_count || 0}</p>
                </div>
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-muted flex items-center justify-center">
                  <CreditCard className="w-5 h-5 lg:w-6 lg:h-6 text-muted-foreground" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-base animate-slide-up" style={{ animationDelay: '150ms' }}>
            <CardContent className="p-4 lg:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Paid Tier</p>
                  <p className="text-2xl lg:text-3xl font-bold font-heading mt-1">{stats?.paid_tier_count || 0}</p>
                </div>
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 lg:w-6 lg:h-6 text-accent" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Tenants */}
        <Card className="card-base animate-slide-up" style={{ animationDelay: '200ms' }}>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              Recent Tenants
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.recent_tenants?.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_tenants.map((tenant) => (
                  <div
                    key={tenant.id}
                    className="flex items-center justify-between p-4 rounded-lg border border-border/50 bg-muted/30"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{tenant.name}</p>
                        <p className="text-sm text-muted-foreground">
                          Created: {formatDate(tenant.created_at)}
                        </p>
                      </div>
                    </div>
                    <span className={`status-badge ${
                      tenant.subscription_tier === 'paid' ? 'status-paid' : 'status-draft'
                    }`}>
                      {tenant.subscription_tier}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No tenants yet.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
